/** Days as `YYYY-MM-DD`, in this browser's zone — the person's day (D22). */

export function isoDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00`);
  date.setDate(date.getDate() + days);
  return isoDay(date);
}

/** `datetime('now')` is UTC with no offset: read it as UTC. */
export function parseStamp(stamp: string): Date {
  return new Date(stamp.includes("T") ? stamp : `${stamp.replace(" ", "T")}Z`);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * A date as a screen shows it: `15 Oct 2026`. The one way the web writes a
 * date, and the phone's `dateText` is the same rule (`core/parity.py`,
 * `date_text`): the two are held to `contract/conformance/date_text.json`.
 * It takes a day, `YYYY-MM-DD`; a moment is made a day first (`isoDay`).
 */
export function dateText(day: string): string {
  const [year, month, date] = day.slice(0, 10).split("-").map(Number);
  const name = MONTHS[(month ?? 0) - 1];
  if (!year || !name || !date) return day;
  return `${date} ${name} ${year}`;
}

/** A moment as a screen shows it: `28 Sep 2026, 21:00`, in this browser's zone. */
export function momentText(at: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dateText(isoDay(at))}, ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * The heading of a day in a list of what was done: "Today", "Yesterday",
 * "Mon 19 Oct" in this year, or the date with its year (R10, as changed on
 * 2026-10-24). Goals' Activity, the piano's sessions and Health share it.
 */
export function dayHeading(day: string, today = isoDay(new Date())): string {
  if (day === today) return "Today";
  if (day === addDays(today, -1)) return "Yesterday";
  if (day.slice(0, 4) !== today.slice(0, 4)) return dateText(day);
  const date = new Date(`${day.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return day;
  return `${WEEKDAYS[date.getDay()]} ${date.getDate()} ${MONTHS[date.getMonth()]}`;
}
